import { useQuery } from '@tanstack/react-query'
import { api } from '../lib/api'
import { Card } from './ui'

interface NodeSystem {
  uptime: string
  top_cpu: string[]
  top_mem: string[]
  connections: number
  listeners: string[]
}

export function NodeSystemBlock({ nodeId }: { nodeId: string }) {
  const { data, isLoading } = useQuery({
    queryKey: ['node-system', nodeId],
    queryFn: async () => (await api.get<NodeSystem>(`/nodes/${nodeId}/system`)).data,
    refetchInterval: 10000,
  })

  if (isLoading || !data) return null

  const parseTop = (lines: string[]) =>
    lines.map(l => {
      const p = l.trim().split(/\s+/)
      // pid user %cpu %mem comm
      if (p.length < 5) return null
      return { pid: p[0], user: p[1], cpu: p[2], mem: p[3], cmd: p.slice(4).join(' ') }
    }).filter(Boolean) as { pid: string; user: string; cpu: string; mem: string; cmd: string }[]

  const topCpu = parseTop(data.top_cpu).slice(0, 6)
  const topMem = parseTop(data.top_mem).slice(0, 6)

  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-3 mb-5">
      <Card className="p-4">
        <div className="text-[11px] uppercase text-neutral-500 tracking-wider mb-1">Uptime</div>
        <div className="text-[15px] font-medium">{data.uptime || '—'}</div>
        <div className="text-[11px] uppercase text-neutral-500 tracking-wider mb-1 mt-4">TCP соединений</div>
        <div className="text-[22px] font-semibold leading-none">{data.connections}</div>
        <div className="text-[11px] uppercase text-neutral-500 tracking-wider mb-2 mt-4">Открытые порты</div>
        <div className="flex flex-wrap gap-1">
          {data.listeners.slice(0, 12).map((l, i) => (
            <span key={i} className="text-[11px] font-mono bg-neutral-100 border border-neutral-200 px-1.5 py-0.5 rounded">{l}</span>
          ))}
          {data.listeners.length > 12 && <span className="text-[11px] text-neutral-400">+{data.listeners.length - 12}</span>}
        </div>
      </Card>

      <Card className="p-4">
        <div className="text-[11px] uppercase text-neutral-500 tracking-wider mb-2">Топ по CPU</div>
        <table className="w-full text-[12px]">
          <tbody>
            {topCpu.map((p, i) => (
              <tr key={i} className="border-b border-neutral-100 last:border-0">
                <td className="py-1 font-mono text-neutral-500 w-10">{p.pid}</td>
                <td className="py-1 text-neutral-600 truncate">{p.cmd}</td>
                <td className="py-1 text-right font-mono text-neutral-900">{p.cpu}%</td>
              </tr>
            ))}
          </tbody>
        </table>
      </Card>

      <Card className="p-4">
        <div className="text-[11px] uppercase text-neutral-500 tracking-wider mb-2">Топ по памяти</div>
        <table className="w-full text-[12px]">
          <tbody>
            {topMem.map((p, i) => (
              <tr key={i} className="border-b border-neutral-100 last:border-0">
                <td className="py-1 font-mono text-neutral-500 w-10">{p.pid}</td>
                <td className="py-1 text-neutral-600 truncate">{p.cmd}</td>
                <td className="py-1 text-right font-mono text-neutral-900">{p.mem}%</td>
              </tr>
            ))}
          </tbody>
        </table>
      </Card>
    </div>
  )
}
